import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Fingerprint, Loader2, LogIn, ScanFace, AlertTriangle, QrCode, Check } from 'lucide-react';
import { useAuthStore } from '../../lib/authStore';
import { startIdleLock } from '../../lib/idleLock';
import {
  getAuthState,
  entraStartUrl,
  enrollPasskey,
  unlockWithPasskey,
  enrollAuthenticateWithPasskey,
  approveQrLogin,
  type AuthUser,
  type UnlockResult,
} from '../../lib/api';
import { AuthOverlay } from './AuthOverlay';
import { importChunk } from '../../lib/chunkReload';

// The QR display (qrcode lib + poller) loads only when the user chooses QR
// sign-in, so it never weighs down the login screen's first paint. importChunk
// self-heals a stale chunk after a deploy (this rarely-loaded chunk is the most
// likely to be missing on a long-open tab) — see lib/chunkReload.ts.
const QrLoginDisplay = lazy(() => importChunk(() => import('./QrLoginDisplay')));

// Map a WebAuthn ceremony error to a friendly message; user-cancel is soft.
function ceremonyError(e: unknown): { message: string; cancelled: boolean } {
  const name = (e as { name?: string })?.name;
  if (name === 'NotAllowedError' || name === 'AbortError') {
    return { message: 'Cancelled — tap to try again.', cancelled: true };
  }
  const msg = (e as { message?: string })?.message;
  return { message: msg ? `Something went wrong: ${msg}` : 'Something went wrong. Try again.', cancelled: false };
}

// True when registration failed because this device's authenticator ALREADY holds
// a passkey for the account (e.g. an iCloud/Google-synced passkey, or a returning
// device whose trust cookie lapsed). @simplewebauthn preserves the DOMException
// name 'InvalidStateError' and tags it with this stable code. The fix: finish
// setup by AUTHENTICATING with that existing passkey instead of re-registering.
function isAlreadyRegistered(e: unknown): boolean {
  const name = (e as { name?: string })?.name;
  const code = (e as { code?: string })?.code;
  return name === 'InvalidStateError' || code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED';
}

export function AuthGate({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);
  const hydrate = useAuthStore((s) => s.hydrate);
  const [bootError, setBootError] = useState(false);

  // Cold-start optimism: if this browser is enrolled and was away longer than its
  // lock window, show the lock immediately (server confirms a beat later).
  useEffect(() => {
    const s = useAuthStore.getState();
    if (s.status === 'loading' && s.enrolled && Date.now() - s.lastVisibleAt > s.lockAfterMs) {
      useAuthStore.setState({ status: 'needs_unlock' });
    }
  }, []);

  // Authoritative state from the server.
  const refresh = () => {
    setBootError(false);
    getAuthState()
      .then((res) => hydrate(res.status, res.user ?? null, res.lockAfterSeconds))
      .catch(() => setBootError(true));
  };
  useEffect(refresh, [hydrate]);

  // Lifecycle / heartbeat (the closed-5-min detector). Safe to call repeatedly.
  useEffect(() => {
    startIdleLock();
  }, []);

  // DEV-only: ?forceWizard (wizard QA) or ?devApp (render the app tree to QA the
  // dashboard itself) bypass the backend boot so the app can be previewed locally.
  // Guarded by import.meta.env.DEV — no effect in prod/INT builds.
  if (
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    (new URLSearchParams(window.location.search).has('forceWizard') ||
      new URLSearchParams(window.location.search).has('devApp'))
  ) {
    return <>{children}</>;
  }

  // Approve deep link (native camera opens …/approve-device#r=REQUESTID on an
  // already signed-in phone). Only meaningful when authenticated — the approver
  // must have a session. Otherwise fall through to the normal sign-in gate.
  if (
    typeof window !== 'undefined' && window.location.pathname === '/approve-device'
    && status === 'authenticated'
  ) {
    return <ApproveDeviceConsume />;
  }

  if (bootError) {
    return (
      <AuthOverlay
        icon={<AlertTriangle size={32} />}
        title="Can't reach the server"
        subtitle="Check your connection and try again."
      >
        <button className="btn btn-primary btn-lg" onClick={refresh}>
          Retry
        </button>
      </AuthOverlay>
    );
  }

  switch (status) {
    case 'loading':
      return (
        <AuthOverlay icon={<Loader2 size={32} className="auth-spin" />} title="Personal Control Plane" />
      );
    case 'authenticated':
    case 'disabled':
      return <>{children}</>;
    case 'needs_entra':
      return <EntraGate />;
    case 'needs_enroll':
      return <EnrollScreen />;
    case 'needs_unlock':
      return <LockScreen />;
    default:
      return <>{children}</>;
  }
}

// ─── Sign in with Microsoft (first visit / re-enroll) ────────────
function EntraGate() {
  const denied = new URLSearchParams(window.location.search).get('auth') === 'denied';
  return (
    <AuthOverlay
      icon={<LogIn size={32} />}
      title="Sign in to continue"
      subtitle={
        denied
          ? 'That account is not allowed. Sign in with an authorized Microsoft account.'
          : 'Verify your identity with Microsoft to set up this device.'
      }
    >
      <button className="btn btn-primary btn-lg" onClick={() => { window.location.href = entraStartUrl(); }}>
        Sign in with Microsoft
      </button>
      <QrLoginButton />
    </AuthOverlay>
  );
}

// ─── Sign in by showing a QR code another (signed-in) device approves ──
// This device displays a QR; an authenticated device scans it from its settings
// and approves; we poll and flip the gate to authenticated on approval.
function QrLoginButton({ disabled }: { disabled?: boolean }) {
  const unlocked = useAuthStore((s) => s.unlocked);
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="btn btn-ghost btn-lg" onClick={() => setOpen(true)} disabled={disabled}>
        <QrCode size={16} /> Sign in with another device
      </button>
      {open && (
        <Suspense fallback={null}>
          <QrLoginDisplay
            onClose={() => setOpen(false)}
            onSuccess={(r) => {
              setOpen(false);
              unlocked(r.user, r.lockAfterSeconds);
              // Fire-and-forget — a cache-refresh hiccup must not throw into the dialog.
              void queryClient.invalidateQueries().catch(() => {});
            }}
          />
        </Suspense>
      )}
    </>
  );
}

// ─── Approve a scanned device-login deep link (…/approve-device#r=REQUESTID) ──
// Reached when an authenticated phone's native camera opens the QR. Explicit tap
// to approve — this signs ANOTHER device into the current account.
function ApproveDeviceConsume() {
  const [phase, setPhase] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const rawId = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('r') ?? '';
  // Only act on a well-formed request id (matches the server's shape) — don't fire
  // junk approve calls for a malformed deep link.
  const requestId = /^[A-Za-z0-9_-]{20,64}$/.test(rawId) ? rawId : '';

  async function run() {
    setPhase('running');
    try {
      await approveQrLogin(requestId);
      setPhase('done');
    } catch (e: unknown) {
      setPhase('error');
      setMessage(e instanceof Error ? e.message : 'Approval failed. Ask the other device for a fresh code.');
    }
  }

  if (!requestId) {
    return (
      <AuthOverlay icon={<QrCode size={32} />} title="Invalid approval link" subtitle="This link is missing its code. Scan the QR again from Profile → Sign in another device.">
        <button className="btn btn-primary btn-lg" onClick={() => { window.location.replace('/'); }}>Go to app</button>
      </AuthOverlay>
    );
  }
  if (phase === 'done') {
    return (
      <AuthOverlay icon={<Check size={32} />} title="Device approved" subtitle="The other device is signing in now. You can close this.">
        <button className="btn btn-primary btn-lg" onClick={() => { window.location.replace('/'); }}>Done</button>
      </AuthOverlay>
    );
  }
  return (
    <AuthOverlay
      icon={phase === 'running' ? <Loader2 size={32} className="auth-spin" /> : <QrCode size={32} />}
      title="Approve sign-in?"
      subtitle={phase === 'error' ? message : 'This signs another device into YOUR account. Only approve a device you are setting up yourself.'}
    >
      <button className="btn btn-primary btn-lg" onClick={run} disabled={phase === 'running'}>
        {phase === 'running' ? 'Approving…' : 'Approve sign-in'}
      </button>
      <button className="btn btn-ghost" onClick={() => { window.location.replace('/'); }} disabled={phase === 'running'}>
        Cancel
      </button>
    </AuthOverlay>
  );
}

// ─── Bind a passkey to this device (after Entra) ─────────────────
function EnrollScreen() {
  const unlocked = useAuthStore((s) => s.unlocked);
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<'intro' | 'running' | 'existing' | 'error'>('intro');
  const [message, setMessage] = useState('');

  async function run() {
    setPhase('running');
    try {
      // Success flips the gate to authenticated and unmounts this screen.
      await finish(enrollPasskey, unlocked, queryClient);
    } catch (e) {
      // This device's authenticator already holds a passkey for the account, so a
      // fresh registration can't be created. Offer to finish setup by signing in
      // with that existing passkey — on a SEPARATE tap, so the unlock ceremony
      // runs under its own user gesture (robust on iOS Safari).
      if (isAlreadyRegistered(e)) {
        setMessage('');
        setPhase('existing');
        return;
      }
      const { message } = ceremonyError(e);
      setPhase('error');
      setMessage(message);
    }
  }

  async function useExisting() {
    setPhase('running');
    try {
      await finish(enrollAuthenticateWithPasskey, unlocked, queryClient);
    } catch (e) {
      // Stay on the existing-passkey screen so a retry uses the right ceremony.
      const { message } = ceremonyError(e);
      setMessage(message);
      setPhase('existing');
    }
  }

  if (phase === 'existing') {
    return (
      <AuthOverlay
        icon={<ScanFace size={32} />}
        title="This device already has a passkey"
        subtitle={message || 'Confirm it with Face ID, Touch ID or your fingerprint to finish setting up this device.'}
      >
        <button className="btn btn-primary btn-lg" onClick={useExisting}>
          Use existing passkey
        </button>
        <button className="btn btn-ghost" onClick={() => { window.location.href = entraStartUrl(); }}>
          Start over
        </button>
      </AuthOverlay>
    );
  }

  return (
    <AuthOverlay
      icon={phase === 'running' ? <Loader2 size={32} className="auth-spin" /> : <Fingerprint size={32} />}
      title="Set up this device"
      subtitle={
        phase === 'error'
          ? message
          : 'Add Face ID, Touch ID or your fingerprint so you can unlock quickly next time.'
      }
    >
      <button className="btn btn-primary btn-lg" onClick={run} disabled={phase === 'running'}>
        {phase === 'running' ? 'Setting up…' : 'Set up biometric unlock'}
      </button>
      <button
        className="btn btn-ghost"
        onClick={() => { window.location.href = entraStartUrl(); }}
        disabled={phase === 'running'}
      >
        Start over
      </button>
    </AuthOverlay>
  );
}

// ─── Biometric unlock (returning, after >5 min closed) ───────────
function LockScreen() {
  const unlocked = useAuthStore((s) => s.unlocked);
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<'idle' | 'running' | 'error'>('idle');
  const [message, setMessage] = useState('');

  async function run() {
    setPhase('running');
    try {
      await finish(unlockWithPasskey, unlocked, queryClient);
    } catch (e) {
      const { message } = ceremonyError(e);
      setPhase('error');
      setMessage(message);
    }
  }

  return (
    <AuthOverlay
      icon={
        phase === 'running' ? <Loader2 size={32} className="auth-spin" /> : <ScanFace size={32} />
      }
      title="Locked"
      subtitle={phase === 'error' ? message : 'Unlock with Face ID, Touch ID or your fingerprint to continue.'}
    >
      <button className="btn btn-primary btn-lg" onClick={run} disabled={phase === 'running'}>
        {phase === 'running' ? 'Unlocking…' : 'Unlock'}
      </button>
      <button
        className="btn btn-ghost"
        onClick={() => { window.location.href = entraStartUrl(); }}
        disabled={phase === 'running'}
      >
        Sign in with Microsoft instead
      </button>
      <QrLoginButton disabled={phase === 'running'} />
    </AuthOverlay>
  );
}

// Shared success path: run the ceremony, flip to active, refetch app data.
async function finish(
  ceremony: () => Promise<UnlockResult>,
  unlocked: (u: AuthUser | null, lockAfterSeconds?: number) => void,
  queryClient: ReturnType<typeof useQueryClient>,
): Promise<void> {
  const { user, lockAfterSeconds } = await ceremony();
  unlocked(user, lockAfterSeconds);
  // Drop any stale authed data cached before the lock; remounted pages refetch.
  await queryClient.invalidateQueries();
}
